
import {
	Plugin
}
from 'ckeditor5';
import {
	PaginationLooker
}
from './pagination-lookup.js';
import {
	PaginationRenderer
}
from './pagination-renderer.js';
import {
	getTranslation
}
from './utils/common-translations.js';

export class PaginationEditing extends Plugin {

	static get pluginName() {
		return 'PaginationEditing';
	}

	static get isOfficialPlugin() {
		return false;
	}

	static get isPremiumPlugin() {
		return false;
	}

	static get requires() {
		return [PaginationLooker, PaginationRenderer];
	}
	
	init() {
		this.set({
			'pageNumber': 1,
			'totalPages': 1
		});
		
		const pagination = this.editor.plugins.get('Pagination');
		const paginationLookUp = this.editor.plugins.get(PaginationLooker);
		const paginationRenderer = this.editor.plugins.get(PaginationRenderer);

		this.bind('isEnabled').to(pagination);

		this._setUpDataDowncastConversion();

		this.bind('pageNumber').to(paginationRenderer);

		this.listenTo(paginationLookUp, 'change:pageBreaks', (_14a918, _0x2e605a, markers, oldMarkers) => {
			this.totalPages = markers.length;
			this._updatePageBreakMarkers(markers, oldMarkers);
		})
		
		this._setupKeystrokes();
	}
	
	destroy() {
		super.destroy()
	}
	
	scrollToPage(pageNumber) {
		if (!this.isEnabled) {
			return;
		}

		this.editor.plugins.get(PaginationRenderer).scrollToPage(this._validatePageNumber(pageNumber));
	}
	
	moveSelectionToPage(pageNumber) {
		if (!this.isEnabled) {
			return;
		}

		const pageLookUp = this.editor.plugins.get(PaginationLooker).pageBreaks[pageNumber - 1];

		if (pageLookUp) {
			this.editor.model.change(model => {
			
				switch (pageLookUp.type) {
					case 'manual': {
						const newSelection = this.editor.model.schema.getNearestSelectionRange(pageLookUp.modelRange.end, 'forward');
						model.setSelection(newSelection);
						break;
					}
					case 'element': {
						const newSelection = this.editor.model.schema.getNearestSelectionRange(pageLookUp.modelRange.start, 'forward');
						model.setSelection(newSelection);
						break;
					}
					case 'text': {
						const rangeStart = pageLookUp.modelRange.start;
						if (rangeStart.textNode && ' ' === rangeStart.textNode.data[rangeStart.offset]) {
							const newSelection = model.createPositionAt(rangeStart.parent, rangeStart.offset + 1);
							model.setSelection(newSelection);
						} else  {
							model.setSelection(pageLookUp.modelRange);
						}
						break;
					}
					default: {
						// no
					}
				}

				this.editor.editing.view.focus();
			});
		}
	}
	
	goToNextPage() {
		const pageNumber = this._validatePageNumber(this.pageNumber + 1);

		this.moveSelectionToPage(pageNumber);
		this.scrollToPage(pageNumber);
	}
	
	goToPreviousPage() {
		const pageNumber = this._validatePageNumber(this.pageNumber - 1);

		this.moveSelectionToPage(pageNumber);
		this.scrollToPage(pageNumber);
	}
	
	_updatePageBreakMarkers(markers, oldMarkers) {
		if (this.isEnabled) { 
			this.editor.model.change(model => {
				for (let currentIndex = 1; currentIndex < markers.length; currentIndex++) {
					const modelRange = markers[currentIndex].modelRange;
					const modelName = 'pagination:' + (currentIndex + 1);

					if (this.editor.model.markers.has(modelName)) {
						model.updateMarker(modelName, {
							'range': modelRange
						});
					} else {
						model.addMarker(modelName, {
							'range': modelRange,
							'usingOperation': false,
							'affectsData': true
						});
					}
				}

				for (let currentIndex = markers.length; currentIndex < oldMarkers.length; currentIndex++) {
					const modelName = 'pagination:' + (currentIndex + 1);

					if(this.editor.model.markers.has(modelName)) {
						model.removeMarker(modelName);
					}
				}
			});
		}
	}
	
	_setUpDataDowncastConversion() {
		const markerLabel = 'pagination';
		this.editor.conversion.for('dataDowncast').add(downCaster => {
			downCaster.on('addMarker:' + markerLabel, (event, { markerRange, markerName }, editor) => {
				
				if (!editor.options.pagination) return;
				if (!editor.consumable.consume(markerRange, event.name)) return;

				const containedElement = markerRange.getContainedElement();

				let editorMapperView = containedElement ? editor.mapper.toViewElement(containedElement) : null;
				if (!editorMapperView) {
					const rangeStart = markerRange.start;
					if (editor.schema.checkChild(rangeStart, '$text')) {
						const editorMapperPosition = editor.mapper.toViewPosition(rangeStart);
						editorMapperView = editor.writer.createContainerElement('span')
						editorMapperView.getFillerOffset = () => null
						editor.writer.insert(editorMapperPosition, editorMapperView);
					} else {
						const currentNode = rangeStart.nodeAfter;
						editorMapperView = editor.mapper.toViewElement(currentNode);
					}
				}

				for (; editorMapperView.parent !== editorMapperView.root && 0x0 === editorMapperView.index;) {
					editorMapperView = editorMapperView.parent;
				}

				const markerLetter = markerName.substring(0xb);

				if (!editorMapperView.hasClass('page-break')) {
					editor.writer.setStyle('page-break-before', 'always', editorMapperView);
				}

				editor.writer.setAttribute('data-pagination-page', markerLetter, editorMapperView);
				editor.mapper.bindElementToMarker(editorMapperView, markerName);

				event.stop();
			});
		});
	}
	
	_setupKeystrokes() {
		this.editor.keystrokes.set('Shift+Pageup', (_, callback) => {
			this.goToPreviousPage();

			callback();
		})
		
		this.editor.keystrokes.set('Shift+Pagedown', (_, callback) => {
			this.goToNextPage();

			callback();
		})
		
		this.editor.accessibility.addKeystrokeInfoGroup({
			'id': 'pagination',
			'label': getTranslation(this.editor.locale, 'Keystrokes for navigating through documents'),
			'keystrokes': [{
				'label': getTranslation(this.editor.locale, 'Go to the previous page (also move selection)'),
				'keystroke': 'Shift+Pageup',
				'mayRequireFn': true
			}, {
				'label': getTranslation(this.editor.locale, 'Go to the next page (also move selection)'),
				'keystroke': 'Shift+Pagedown',
				'mayRequireFn': true
			}]
		});
	}
	
	_validatePageNumber(pageNumber) {
		if (pageNumber < 1) {
			pageNumber = 1;
		}

		if (pageNumber > this.totalPages) {
			pageNumber = this.totalPages;
		}

		return pageNumber;
	}
}